import { useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  Box,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  ToggleButtonGroup,
  Tooltip,
} from '@mui/material'
import { ChartExportWrapper, StackedBarChart, ToggleButton } from 'components'
import { applyAndFetchChart } from 'store/slices/dataAnalysisSlice'
import EmptyState from 'pages/DataAnalysis/components/EmptyState'
import { TConsistencyTrendData } from 'types/models/DataAnalysis'
import {
  selectChartData,
  selectChartStatus,
} from 'store/selectors/dataAnalysisSelectors'
import DataAnalysisFilters from '../../components/DataAnalysisFilters/DataAnalysisFilters'
import { getTabByKey, prettyAttr, TAB_KEYS } from '../../utils/consts'
import { toConsistencyTrendChartData } from '../../transforms'
import { DataTabsLayout, TrendChartStatus } from '../../layouts'
import normalizeDataset from '../../utils/normalizeDataset'
import * as sxStyles from '../tabs.styles'

export const ConsistencyTrend = () => {
  const dispatch = useDispatch()
  const rawData = useSelector(selectChartData(TAB_KEYS.CONSISTENCY_TREND))
  const status = useSelector(selectChartStatus(TAB_KEYS.CONSISTENCY_TREND))
  const currentTab = getTabByKey(TAB_KEYS.CONSISTENCY_TREND)

  const [mode, setMode] = useState<'count' | 'percentage'>('count')

  const data = useMemo(
    () => normalizeDataset<TConsistencyTrendData>(rawData as any),
    [rawData]
  )
  const { rows, series } = useMemo(
    () => toConsistencyTrendChartData(data, mode),
    [data, mode]
  )

  const cleanedRows = rows.map((item) => ({
    ...item,
    wheatLabel: item.wheatLabel.replace(/^Wheat\s*/i, ''),
  }))

  const hasAnyData = rows.length > 0

  return (
    <DataTabsLayout
      sidebar={
        <DataAnalysisFilters
          filtersList={currentTab?.filters ?? []}
          chartKey={TAB_KEYS.CONSISTENCY_TREND}
        />
      }
      tabKey={TAB_KEYS.CONSISTENCY_TREND}
      onApply={() =>
        dispatch(applyAndFetchChart({ key: TAB_KEYS.CONSISTENCY_TREND }))
      }
    >
      <TrendChartStatus status={status} title={TAB_KEYS.CONSISTENCY_TREND}>
        {!hasAnyData ? (
          <EmptyState />
        ) : (
          <Box display="flex" justifyContent="center" width="100%">
            <Card
              data-exportable-chart="true"
              variant="outlined"
              sx={{
                ...sxStyles.parametersComparisonCard,
                width: '100%',
              }}
            >
              <CardHeader
                title={prettyAttr('Consistency Trend')}
                className="no-print"
                titleTypographyProps={{ fontSize: 24, fontWeight: 800 }}
                action={
                  <ToggleButtonGroup
                    size="small"
                    value={mode}
                    exclusive
                    onChange={(_, val) => val && setMode(val)}
                  >
                    <Tooltip title="Raw Count">
                      <span>
                        <ToggleButton value="count">#</ToggleButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Percentage">
                      <span>
                        <ToggleButton value="percentage">%</ToggleButton>
                      </span>
                    </Tooltip>
                  </ToggleButtonGroup>
                }
              />
              <CardContent
                sx={{
                  ...sxStyles.parametersComparisonCardContent,
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <ChartExportWrapper
                    title="Consistency Trend"
                    actionsRightPadding={48}
                  >
                    <StackedBarChart
                      data={cleanedRows}
                      categoryKey="wheatLabel"
                      series={series}
                      percentage={mode === 'percentage'}
                      totalKey="total"
                      tooltipKey="score"
                      tooltipRender={(v) => <div>Score: {v}%</div>}
                      minLabelValue={1}
                      orientation="vertical"
                    />
                  </ChartExportWrapper>
                </Box>
              </CardContent>
              <CardActions
                className="no-print"
                sx={{ justifyContent: 'center', paddingBottom: 2 }}
              >
                <span style={{ opacity: 0.7, fontSize: 16 }}>
                  Score is calculated as the total of Consistent and Borderline
                  deliveries.
                </span>
              </CardActions>
            </Card>
          </Box>
        )}
      </TrendChartStatus>
    </DataTabsLayout>
  )
}
